/**
 * Fidelite – Programme de Fidélité
 *
 * This file is the layout orchestrator only.
 * All rendering logic lives in ./components/**
 * All constants live in ./constants.js
 * The API hook lives in ./hooks/useLoyaltyApi.js
 */
import React, { useState, useEffect } from "react";
import {
  Box, Paper, Grid, Button, Avatar, Typography,
  Tabs, Tab, Divider, Snackbar, Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

// Icons used in this layout file
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import StarIcon from "@mui/icons-material/Star";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import RedeemIcon from "@mui/icons-material/Redeem";
import GavelIcon from "@mui/icons-material/Gavel";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useBrand } from "../../contexts/BrandContext";
import { LOYALTY_API_BASE, SECTION_COLORS } from "./constants";
import { useLoyaltyApi } from "./hooks/useLoyaltyApi";

// ── Shared UI ────────────────────────────────────────────────
import StatCard from "./components/shared/StatCard";
import SectionHeader from "./components/shared/SectionHeader";

// ── Customer lookup / upsert ───────────────────────────────
import CustomerLookupDialog from "./components/CustomerLookupDialog";
import CustomerUpsertDialog from "./components/CustomerUpsertDialog";

// ── Tiers ────────────────────────────────────────────────────
import TiersTab from "./components/tiers/TiersTab";
import CreateTierDialog from "./components/tiers/CreateTierDialog";
import ViewTierDialog from "./components/tiers/ViewTierDialog";
import EditTierDialog from "./components/tiers/EditTierDialog";
import DeleteTierDialog from "./components/tiers/DeleteTierDialog";

// ── Rewards ──────────────────────────────────────────────────
import RewardsTab from "./components/rewards/RewardsTab";
import CreateRewardDialog from "./components/rewards/CreateRewardDialog";
import ViewRewardDialog from "./components/rewards/ViewRewardDialog";
import EditRewardDialog from "./components/rewards/EditRewardDialog";
import DeleteRewardDialog from "./components/rewards/DeleteRewardDialog";

// ── Bonus Definitions ────────────────────────────────────────
import BonusTab from "./components/bonus/BonusTab";
import CreateBonusDialog from "./components/bonus/CreateBonusDialog";
import ViewBonusDialog from "./components/bonus/ViewBonusDialog";
import EditBonusDialog from "./components/bonus/EditBonusDialog";
import DeleteBonusDialog from "./components/bonus/DeleteBonusDialog";

// ── Règles ──────────────────────────────────────────────────
import RulesTab from "./components/rules/RulesTab";
import CreateRuleDialog from "./components/rules/CreateRuleDialog";
import ViewRuleDialog from "./components/rules/ViewRuleDialog";
import EditRuleDialog from "./components/rules/EditRuleDialog";
import DeleteRuleDialog from "./components/rules/DeleteRuleDialog";

// ── Types d'Événements ──────────────────────────────────────
import EventTypesTab from "./components/eventTypes/EventTypesTab";
import CreateEventTypeDialog from "./components/eventTypes/CreateEventTypeDialog";
import ViewEventTypeDialog from "./components/eventTypes/ViewEventTypeDialog";
import EditEventTypeDialog from "./components/eventTypes/EditEventTypeDialog";
import DeleteEventTypeDialog from "./components/eventTypes/DeleteEventTypeDialog";

// ─────────────────────────────────────────────────────────────
// Simple table renders for the read-only sections
// (Rules, EventTypes — CRUD coming soon)
// ─────────────────────────────────────────────────────────────

// renderBonusDefs has been replaced by <BonusTab /> (see components/bonus/)

// renderRules has been replaced by <RulesTab /> (see components/rules/)

// renderEventTypes has been replaced by <EventTypesTab /> (see components/eventTypes/)

// ─────────────────────────────────────────────────────────────
// Main Orchestrator Component
// ─────────────────────────────────────────────────────────────
export default function Fidelite() {
  const navigate = useNavigate();
  const { currentBrand } = useBrand();
  const brandName = currentBrand?.name || "";

  // ── Tab ──────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);

  // ── Dialog visibility ────────────────────────────────────
  const [searchOpen, setSearchOpen] = useState(false);
  const [upsertOpen, setUpsertOpen] = useState(false);
  const [createTierOpen, setCreateTierOpen] = useState(false);
  const [viewTier, setViewTier] = useState(null);
  const [editTier, setEditTier] = useState(null);
  const [deleteTier, setDeleteTier] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [createRewardOpen, setCreateRewardOpen] = useState(false);
  const [viewReward, setViewReward] = useState(null);
  const [editReward, setEditReward] = useState(null);
  const [deleteReward, setDeleteReward] = useState(null);
  const [deletingReward, setDeletingReward] = useState(false);

  // ── Bonus dialog visibility ───────────────────────────────
  const [createBonusOpen, setCreateBonusOpen] = useState(false);
  const [viewBonus, setViewBonus] = useState(null);
  const [editBonus, setEditBonus] = useState(null);
  const [deleteBonus, setDeleteBonus] = useState(null);
  const [deletingBonus, setDeletingBonus] = useState(false);

  // ── Rules dialog visibility ───────────────────────────────
  const [createRuleOpen, setCreateRuleOpen] = useState(false);
  const [viewRule, setViewRule] = useState(null);
  const [editRule, setEditRule] = useState(null);
  const [deleteRule, setDeleteRule] = useState(null);
  const [deletingRule, setDeletingRule] = useState(false);

  // ── EventTypes dialog visibility ───────────────────────
  const [createEventTypeOpen, setCreateEventTypeOpen] = useState(false);
  const [viewEventType, setViewEventType] = useState(null);
  const [editEventType, setEditEventType] = useState(null);
  const [deleteEventType, setDeleteEventType] = useState(null);
  const [deletingEventType, setDeletingEventType] = useState(false);

  // ── Snackbar ─────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  // ── Pagination state (one slot per tab) ──────────────────
  const [pages, setPages] = useState([0, 0, 0, 0, 0]);
  const handlePageChange = (tabIdx) => (_, newPage) =>
    setPages((prev) => { const n = [...prev]; n[tabIdx] = newPage; return n; });

  // ── Brand-filtered API hooks ──────────────────────────────
  const brandQuery = brandName ? `?brand=${encodeURIComponent(brandName)}` : "";
  const tiers = useLoyaltyApi(`/admin/loyalty-tiers${brandQuery}`, !!brandName);
  const rewards = useLoyaltyApi(`/rewards${brandQuery}`, !!brandName);
  const bonusDefs = useLoyaltyApi(`/admin/bonus-definitions${brandQuery}`, !!brandName);
  const rules = useLoyaltyApi(`/rules${brandQuery}`, !!brandName);
  const eventTypes = useLoyaltyApi(`/admin/event-types${brandQuery}`, !!brandName);

  // Reset pagination when brand changes
  useEffect(() => {
    setPages([0, 0, 0, 0, 0]);
  }, [brandName]);

  // ── Summary stats ─────────────────────────────────────────
  const tiersCount = Array.isArray(tiers.data) ? tiers.data.length : 0;
  const tiersActive = Array.isArray(tiers.data) ? tiers.data.filter((t) => t.active).length : 0;
  const rewardsCount = Array.isArray(rewards.data) ? rewards.data.length : 0;
  const rewardsActive = Array.isArray(rewards.data) ? rewards.data.filter((r) => r.active).length : 0;
  const bonusCount = Array.isArray(bonusDefs.data) ? bonusDefs.data.length : 0;
  const bonusActive = Array.isArray(bonusDefs.data) ? bonusDefs.data.filter((b) => b.active).length : 0;
  const eventTypesCount = Array.isArray(eventTypes.data) ? eventTypes.data.length : 0;
  const eventTypesActive = Array.isArray(eventTypes.data) ? eventTypes.data.filter((e) => e.active).length : 0;

  const tabLoading = [tiers.loading, rewards.loading, bonusDefs.loading, rules.loading, eventTypes.loading];

  const handleRefreshAll = () => {
    tiers.refetch(); rewards.refetch(); bonusDefs.refetch();
    rules.refetch(); eventTypes.refetch();
    notify("Données rafraîchies");
  };

  // ── Tier CRUD handlers ────────────────────────────────────
  const handleDeleteTierConfirmed = async () => {
    if (!deleteTier) return;
    setDeleting(true);
    try {
      const res = await fetch(`${LOYALTY_API_BASE}/admin/loyalty-tiers/${deleteTier.id}`, {
        method: "DELETE",
        headers: { "X-Brand": brandName }
      });
      if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
      setDeleteTier(null);
      tiers.refetch();
      notify(`Niveau "${deleteTier.name}" supprimé avec succès`);
    } catch (err) { notify(err.message, "error"); } finally { setDeleting(false); }
  };

  // ── Reward CRUD handlers ──────────────────────────────────
  const handleDeleteRewardConfirmed = async () => {
    if (!deleteReward) return;
    setDeletingReward(true);
    try {
      const res = await fetch(`${LOYALTY_API_BASE}/rewards/${deleteReward.id}`, {
        method: "DELETE",
        headers: { "X-Brand": brandName }
      });
      if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
      setDeleteReward(null);
      rewards.refetch();
      notify(`Récompense "${deleteReward.name}" supprimée avec succès`);
    } catch (err) { notify(err.message, "error"); } finally { setDeletingReward(false); }
  };

  // ── Bonus CRUD handlers ───────────────────────────────────
  const handleDeleteBonusConfirmed = async () => {
    if (!deleteBonus) return;
    setDeletingBonus(true);
    try {
      const res = await fetch(`${LOYALTY_API_BASE}/admin/bonus-definitions/${deleteBonus.id}`, {
        method: "DELETE",
        headers: { "X-Brand": brandName }
      });
      if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
      setDeleteBonus(null);
      bonusDefs.refetch();
      notify(`Définition "${deleteBonus.name}" supprimée avec succès`);
    } catch (err) { notify(err.message, "error"); } finally { setDeletingBonus(false); }
  };

  // ── Rules CRUD handlers ───────────────────────────────────
  const handleDeleteRuleConfirmed = async () => {
    if (!deleteRule) return;
    setDeletingRule(true);
    try {
      const res = await fetch(`${LOYALTY_API_BASE}/rules/${deleteRule.id}`, {
        method: "DELETE",
        headers: { "X-Brand": brandName }
      });
      if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
      setDeleteRule(null);
      rules.refetch();
      notify(`Règle "${deleteRule.event_type}" supprimée avec succès`);
    } catch (err) { notify(err.message, "error"); } finally { setDeletingRule(false); }
  };

  // ── Tab configuration ─────────────────────────────────────
  const tabItems = [
    { label: "Niveaux", icon: <StarIcon sx={{ fontSize: 16 }} /> },
    { label: "Récompenses", icon: <CardGiftcardIcon sx={{ fontSize: 16 }} /> },
    { label: "Bonus", icon: <RedeemIcon sx={{ fontSize: 16 }} /> },
    { label: "Règles", icon: <GavelIcon sx={{ fontSize: 16 }} /> },
    { label: "Types d'Événements", icon: <EventNoteIcon sx={{ fontSize: 16 }} /> },
  ];
  const tabColors = [
    SECTION_COLORS.tiers, SECTION_COLORS.rewards, SECTION_COLORS.bonus,
    SECTION_COLORS.rules, SECTION_COLORS.eventTypes,
  ];
  const tabRefresh = [tiers.refetch, rewards.refetch, bonusDefs.refetch, rules.refetch, eventTypes.refetch];

  // ── Tab content ───────────────────────────────────────────
  const tabContent = [
    <TiersTab
      tiersApi={tiers} pages={pages} handlePageChange={handlePageChange}
      onView={setViewTier} onEdit={setEditTier} onDelete={setDeleteTier}
    />,
    <RewardsTab
      rewardsApi={rewards} pages={pages} handlePageChange={handlePageChange}
      onView={setViewReward} onEdit={setEditReward} onDelete={setDeleteReward}
      onAdd={() => setCreateRewardOpen(true)}
    />,
    <BonusTab
      bonusApi={bonusDefs} pages={pages} handlePageChange={handlePageChange}
      onView={setViewBonus} onEdit={setEditBonus} onDelete={setDeleteBonus}
      onAdd={() => setCreateBonusOpen(true)}
    />,
    <RulesTab
      rulesApi={rules} pages={pages} handlePageChange={handlePageChange}
      onView={setViewRule} onEdit={setEditRule} onDelete={setDeleteRule}
    />,
    <EventTypesTab
      eventTypesApi={eventTypes} pages={pages} handlePageChange={handlePageChange}
      onView={setViewEventType} onEdit={setEditEventType} onDelete={setDeleteEventType}
    />,
  ];

  return (
    <>
      {/* ── Dialogs ────────────────────────────────────────── */}
      <CustomerLookupDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
      <CustomerUpsertDialog open={upsertOpen} onClose={() => setUpsertOpen(false)} onSaved={(msg) => notify(msg, "success")} />

      {/* Tiers */}
      <CreateTierDialog
        open={createTierOpen}
        onClose={() => setCreateTierOpen(false)}
        onCreated={() => { tiers.refetch(); notify("Niveau de fidélité créé avec succès"); }}
      />
      <ViewTierDialog
        open={Boolean(viewTier)} tier={viewTier}
        onClose={() => setViewTier(null)} onEditRequest={setEditTier}
      />
      <EditTierDialog
        open={Boolean(editTier)} tier={editTier}
        onClose={() => setEditTier(null)}
        onSaved={() => { tiers.refetch(); notify("Niveau mis à jour avec succès"); }}
      />
      <DeleteTierDialog
        open={Boolean(deleteTier)} tier={deleteTier}
        onClose={() => !deleting && setDeleteTier(null)}
        onConfirm={handleDeleteTierConfirmed} deleting={deleting}
      />

      {/* Rewards */}
      <CreateRewardDialog
        open={createRewardOpen}
        onClose={() => setCreateRewardOpen(false)}
        onCreated={() => { rewards.refetch(); notify("Récompense créée avec succès"); }}
      />
      <ViewRewardDialog
        open={Boolean(viewReward)} reward={viewReward}
        onClose={() => setViewReward(null)} onEditRequest={setEditReward}
      />
      <EditRewardDialog
        open={Boolean(editReward)} reward={editReward}
        onClose={() => setEditReward(null)}
        onSaved={() => { rewards.refetch(); notify("Récompense mise à jour avec succès"); }}
      />
      <DeleteRewardDialog
        open={Boolean(deleteReward)} reward={deleteReward}
        onClose={() => !deletingReward && setDeleteReward(null)}
        onConfirm={handleDeleteRewardConfirmed} deleting={deletingReward}
      />

      {/* Bonus Definitions */}
      <CreateBonusDialog
        open={createBonusOpen}
        onClose={() => setCreateBonusOpen(false)}
        onCreated={() => { bonusDefs.refetch(); notify("Définition de bonus créée avec succès"); }}
      />
      <ViewBonusDialog
        open={Boolean(viewBonus)} bonus={viewBonus}
        onClose={() => setViewBonus(null)} onEditRequest={setEditBonus}
      />
      <EditBonusDialog
        open={Boolean(editBonus)} bonus={editBonus}
        onClose={() => setEditBonus(null)}
        onSaved={() => { bonusDefs.refetch(); notify("Définition mise à jour avec succès"); }}
      />
      <DeleteBonusDialog
        open={Boolean(deleteBonus)} bonus={deleteBonus}
        onClose={() => !deletingBonus && setDeleteBonus(null)}
        onConfirm={handleDeleteBonusConfirmed} deleting={deletingBonus}
      />

      {/* Règles */}
      <CreateRuleDialog
        open={createRuleOpen}
        onClose={() => setCreateRuleOpen(false)}
        onCreated={() => { rules.refetch(); notify("Règle créée avec succès"); }}
      />
      <ViewRuleDialog
        open={Boolean(viewRule)} rule={viewRule}
        onClose={() => setViewRule(null)} onEditRequest={setEditRule}
      />
      <EditRuleDialog
        open={Boolean(editRule)} rule={editRule}
        onClose={() => setEditRule(null)}
        onSaved={() => { rules.refetch(); notify("Règle mise à jour avec succès"); }}
      />
      <DeleteRuleDialog
        open={Boolean(deleteRule)} rule={deleteRule}
        onClose={() => !deletingRule && setDeleteRule(null)}
        onConfirm={handleDeleteRuleConfirmed} deleting={deletingRule}
      />

      {/* Types d'Événements */}
      <CreateEventTypeDialog
        open={createEventTypeOpen}
        onClose={() => setCreateEventTypeOpen(false)}
        onCreated={() => { eventTypes.refetch(); notify("Type d'événement créé avec succès"); }}
      />
      <ViewEventTypeDialog
        open={Boolean(viewEventType)} eventType={viewEventType}
        onClose={() => setViewEventType(null)} onEditRequest={setEditEventType}
      />
      <EditEventTypeDialog
        open={Boolean(editEventType)} eventType={editEventType}
        onClose={() => setEditEventType(null)}
        onSaved={() => { eventTypes.refetch(); notify("Type d'événement mis à jour avec succès"); }}
      />
      <DeleteEventTypeDialog
        open={Boolean(deleteEventType)} eventType={deleteEventType}
        onClose={() => !deletingEventType && setDeleteEventType(null)}
        onConfirm={async () => {
          if (!deleteEventType) return;
          setDeletingEventType(true);
          try {
            const res = await fetch(`${LOYALTY_API_BASE}/admin/event-types/${deleteEventType.id}`, {
              method: "DELETE",
              headers: { "X-Brand": brandName }
            });
            if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
            setDeleteEventType(null);
            eventTypes.refetch();
            notify(`Type "${deleteEventType.name}" supprimé avec succès`);
          } catch (err) { notify(err.message, "error"); } finally { setDeletingEventType(false); }
        }}
        deleting={deletingEventType}
      />

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ borderRadius: 2, fontFamily: "Montserrat, sans-serif" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── Page Layout ──────────────────────────────────────── */}
      <Box bgcolor="#f5f7fa" minHeight="100vh" p={{ xs: 1, md: 3 }}>
        <Box maxWidth={1200} mx="auto">

          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar sx={{ width: 44, height: 44, background: "linear-gradient(135deg, #6C63FF 0%, #A855F7 100%)", boxShadow: "0 4px 16px rgba(108, 99, 255, 0.3)" }}>
                <LoyaltyIcon sx={{ fontSize: 24 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={800} fontFamily="Montserrat, sans-serif"
                  sx={{ background: "linear-gradient(135deg, #1a1a2e 0%, #6C63FF 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  Programme de Fidélité
                </Typography>
                <Typography fontSize={12} color="text.secondary" fontFamily="Montserrat, sans-serif">
                  Gestion centralisée du moteur de fidélité
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <Button variant="outlined" onClick={() => setUpsertOpen(true)} startIcon={<PersonAddIcon />}
                sx={{
                  borderRadius: 2, textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                  borderColor: SECTION_COLORS.search.main, color: SECTION_COLORS.search.main,
                  "&:hover": { borderColor: SECTION_COLORS.search.main, bgcolor: SECTION_COLORS.search.light }
                }}>
                Ajouter un Client
              </Button>
              <Button variant="outlined" onClick={() => setSearchOpen(true)} startIcon={<PersonSearchIcon />}
                sx={{
                  borderRadius: 2, textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                  borderColor: SECTION_COLORS.search.main, color: SECTION_COLORS.search.main,
                  "&:hover": { borderColor: SECTION_COLORS.search.main, bgcolor: SECTION_COLORS.search.light }
                }}>
                Consulter un Client
              </Button>
              <Button variant="contained" onClick={handleRefreshAll} startIcon={<RefreshIcon />}
                sx={{
                  borderRadius: 2, textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                  background: "linear-gradient(135deg, #6C63FF 0%, #A855F7 100%)",
                  boxShadow: "0 4px 12px rgba(108, 99, 255, 0.25)",
                  "&:hover": { boxShadow: "0 6px 20px rgba(108, 99, 255, 0.35)" }
                }}>
                Rafraîchir Tout
              </Button>
            </Box>
          </Box>

          {/* KPI Cards */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<StarIcon sx={{ fontSize: 20 }} />} label="Niveaux de Fidélité"
                value={`${tiersActive} / ${tiersCount}`} color={SECTION_COLORS.tiers.main} loading={tiers.loading} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<CardGiftcardIcon sx={{ fontSize: 20 }} />} label="Récompenses"
                value={`${rewardsActive} / ${rewardsCount}`} color={SECTION_COLORS.rewards.main} loading={rewards.loading} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<RedeemIcon sx={{ fontSize: 20 }} />} label="Définitions Bonus"
                value={`${bonusActive} / ${bonusCount}`} color={SECTION_COLORS.bonus.main} loading={bonusDefs.loading} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard icon={<EventNoteIcon sx={{ fontSize: 20 }} />} label="Types d'Événements"
                value={`${eventTypesActive} / ${eventTypesCount}`} color={SECTION_COLORS.eventTypes.main} loading={eventTypes.loading} />
            </Grid>
          </Grid>

          {/* Tabbed Content */}
          <Paper elevation={0} sx={{ borderRadius: 4, border: "1px solid #e8ecf1", overflow: "hidden", background: "#fff", boxShadow: "0 1px 8px rgba(0,0,0,0.04)" }}>
            {/* Tab bar */}
            <Box sx={{ borderBottom: "2px solid #f1f3f5", background: "linear-gradient(180deg, #fafbfd 0%, #fff 100%)" }}>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
                variant="scrollable" scrollButtons="auto"
                sx={{
                  px: 2,
                  "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif", fontSize: 13, minHeight: 52, color: "#64748b", transition: "all 0.2s", "&:hover": { color: "#1a1a2e" } },
                  "& .Mui-selected": { color: `${tabColors[activeTab]?.main} !important` },
                  "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0", background: tabColors[activeTab]?.gradient },
                }}>
                {tabItems.map((tab, idx) => (
                  <Tab key={idx} label={
                    <Box display="flex" alignItems="center" gap={0.75}>
                      {tab.icon}
                      {tab.label}
                    </Box>
                  } />
                ))}
              </Tabs>
            </Box>

            {/* Tab panel */}
            <Box p={{ xs: 2, md: 3 }} minHeight={300}>
              <SectionHeader
                icon={tabItems[activeTab].icon}
                title={tabItems[activeTab].label}
                color={tabColors[activeTab]}
                onRefresh={tabRefresh[activeTab]}
                refreshing={tabLoading[activeTab]}
              >
                {/* "Ajouter" buttons injected per-tab */}
                {activeTab === 1 && (
                  <Button size="small" variant="contained" startIcon={<AddIcon />}
                    onClick={() => setCreateRewardOpen(true)}
                    sx={{
                      textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                      fontSize: 12, borderRadius: 2, background: SECTION_COLORS.rewards.gradient,
                      boxShadow: `0 2px 8px ${SECTION_COLORS.rewards.main}30`,
                      "&:hover": { boxShadow: `0 4px 14px ${SECTION_COLORS.rewards.main}40` }
                    }}>
                    Ajouter une Récompense
                  </Button>
                )}
                {activeTab === 2 && (
                  <Button size="small" variant="contained" startIcon={<AddIcon />}
                    onClick={() => setCreateBonusOpen(true)}
                    sx={{
                      textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                      fontSize: 12, borderRadius: 2, background: SECTION_COLORS.bonus.gradient,
                      boxShadow: `0 2px 8px ${SECTION_COLORS.bonus.main}30`,
                      "&:hover": { boxShadow: `0 4px 14px ${SECTION_COLORS.bonus.main}40` }
                    }}>
                    Ajouter un Bonus
                  </Button>
                )}
                {activeTab === 3 && (
                  <Button size="small" variant="contained" startIcon={<AddIcon />}
                    onClick={() => setCreateRuleOpen(true)}
                    sx={{
                      textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                      fontSize: 12, borderRadius: 2, background: SECTION_COLORS.rules.gradient,
                      boxShadow: `0 2px 8px ${SECTION_COLORS.rules.main}30`,
                      "&:hover": { boxShadow: `0 4px 14px ${SECTION_COLORS.rules.main}40` }
                    }}>
                    Ajouter une Règle
                  </Button>
                )}
                {activeTab === 4 && (
                  <Button size="small" variant="contained" startIcon={<AddIcon />}
                    onClick={() => setCreateEventTypeOpen(true)}
                    sx={{
                      textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                      fontSize: 12, borderRadius: 2, background: SECTION_COLORS.eventTypes.gradient,
                      boxShadow: `0 2px 8px ${SECTION_COLORS.eventTypes.main}30`,
                      "&:hover": { boxShadow: `0 4px 14px ${SECTION_COLORS.eventTypes.main}40` }
                    }}>
                    Ajouter un Type d'Événement
                  </Button>
                )}
                {activeTab === 0 && (
                  <Button size="small" variant="contained" startIcon={<AddIcon />}
                    onClick={() => setCreateTierOpen(true)}
                    sx={{
                      textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                      fontSize: 12, borderRadius: 2, background: SECTION_COLORS.tiers.gradient,
                      boxShadow: `0 2px 8px ${SECTION_COLORS.tiers.main}30`,
                      "&:hover": { boxShadow: `0 4px 14px ${SECTION_COLORS.tiers.main}40` }
                    }}>
                    Ajouter un Niveau
                  </Button>
                )}
              </SectionHeader>

              <Divider sx={{ mb: 2 }} />

              {tabContent[activeTab]}
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}
